import {
  productsClepitSummary,
  productsDevjujuSummary,
  productsEstimojoSummary,
  productsGamblishSummary,
  productsGlanceConsole,
  productsGlanceDocs,
  productsGlanceGraphql,
  productsGlanceIngest,
  productsGlanceMcp,
  productsGlancePublished,
  productsGlanceRealtime,
  productsGlanceReplay,
  productsNjangySummary,
  productsPieleafSummary,
  productsProdantixSummary,
  productsWorkadabraSummary,
} from 'Bomdisoft/translations';
import type { Translation } from '@bomdisoft/translations';

/**
 * A public address the product answers on, beyond its own site. URLs come from
 * `infra/iac/projects/<product>` rather than from docs prose, so what this page
 * publishes is what Tofu actually provisions. Internal surfaces, such as
 * `admin.prodantix.com`, are deliberately absent.
 */
export type CatalogLink = { label: Translation; url: string };

/**
 * `host` is the product's own declared `productionHost`, restated rather than
 * imported because the import rules forbid reaching into a sibling product's
 * tree. A link from the hub must land on the page that claims that URL as its
 * canonical, not on a host that redirects away from it, so these are asserted by
 * name in the spec beside this file.
 */
export type CatalogProduct = {
  applicationCategory: string;
  host: string;
  /** Product screenshot where one exists, otherwise the product mark. */
  image: string;
  links: readonly CatalogLink[];
  logo: string;
  name: string;
  slug: string;
  summary: Translation;
};

/**
 * Product priority order, not alphabetical, so this page leads with the same two
 * products as the hero grid, the carousel and the apps menu. The first six are
 * the order locked by the user on 2026-08-23 and live in `shared/products.ts`.
 *
 * Pieleaf and Devjuju are not in that list, so their placement comes from the
 * Rust migration program order in `AGENTS.md`, which agrees with
 * the locked order on all five products they share and puts pieleaf ahead of
 * devjuju.
 */
export const productCatalog: readonly CatalogProduct[] = [
  {
    applicationCategory: 'DeveloperApplication',
    host: 'prodantix.com',
    image: '/images/projects/prodantix.jpg',
    links: [
      { label: productsGlanceConsole, url: 'https://app.prodantix.com' },
      { label: productsGlanceDocs, url: 'https://prodantix.com/en/docs/overview' },
      { label: productsGlanceGraphql, url: 'https://api.prodantix.com/graphql' },
      { label: productsGlanceIngest, url: 'https://eu.api.prodantix.com/v1/events' },
      { label: productsGlanceRealtime, url: 'https://eu.ws.prodantix.com' },
      { label: productsGlanceMcp, url: 'https://eu.mcp.prodantix.com/mcp' },
      { label: productsGlanceReplay, url: 'https://eu.replay.prodantix.com' },
    ],
    logo: '/images/products/prodantix.png',
    name: 'Prodantix',
    slug: 'prodantix',
    summary: productsProdantixSummary,
  },
  {
    applicationCategory: 'DeveloperApplication',
    host: 'clepit.com',
    image: '/images/projects/clepit.jpg',
    links: [
      { label: productsGlanceConsole, url: 'https://app.clepit.com' },
      { label: productsGlanceDocs, url: 'https://clepit.com/en/docs' },
      { label: productsGlanceGraphql, url: 'https://api.clepit.com/graphql' },
      { label: productsGlanceRealtime, url: 'https://ws.clepit.com' },
      { label: productsGlanceMcp, url: 'https://api.clepit.com/mcp' },
      { label: productsGlancePublished, url: 'https://clepit.space' },
    ],
    logo: '/images/products/clepit.png',
    name: 'Clepit',
    slug: 'clepit',
    summary: productsClepitSummary,
  },
  { applicationCategory: 'FinanceApplication', host: 'www.njangy.com', image: '/images/projects/njangy.jpg', links: [], logo: '/images/products/njangy.png', name: 'Njangy', slug: 'njangy', summary: productsNjangySummary },
  { applicationCategory: 'GameApplication', host: 'www.gamblish.org', image: '/images/projects/gamblish.jpg', links: [], logo: '/images/products/gamblish.png', name: 'Gamblish', slug: 'gamblish', summary: productsGamblishSummary },
  { applicationCategory: 'BusinessApplication', host: 'www.workadabra.com', image: '/images/projects/workadabra.jpg', links: [], logo: '/images/products/workadabra.png', name: 'Workadabra', slug: 'workadabra', summary: productsWorkadabraSummary },
  { applicationCategory: 'BusinessApplication', host: 'estimojo.bomdisoft.com', image: '/images/projects/estimojo.jpg', links: [], logo: '/images/products/estimojo.png', name: 'Estimojo', slug: 'estimojo', summary: productsEstimojoSummary },
  { applicationCategory: 'ShoppingApplication', host: 'www.pieleaf.com', image: '/images/products/pieleaf.png', links: [], logo: '/images/products/pieleaf.png', name: 'Pieleaf', slug: 'pieleaf', summary: productsPieleafSummary },
  { applicationCategory: 'EducationalApplication', host: 'www.devjuju.com', image: '/images/products/devjuju.png', links: [], logo: '/images/products/devjuju.png', name: 'Devjuju', slug: 'devjuju', summary: productsDevjujuSummary },
];

export const findCatalogProduct = (slug: string): CatalogProduct | undefined => productCatalog.find(product => product.slug === slug);
